import type { IconCode } from "@components/Atoms/Icon/LucideIcon";
import t from "@ext/localization/locale/translate";
import getIconColor from "@ext/markdown/elements/note/edit/logic/getNoteButtonIconColor";
import { NoteType, noteIcons } from "@ext/markdown/elements/note/render/component/Note";
import type { Editor } from "@tiptap/core";
import type { Node } from "@tiptap/pm/model";
import {
	DropdownMenu,
	DropdownMenuContent,
	DropdownMenuRadioGroup,
	DropdownMenuRadioItem,
	DropdownMenuTrigger,
} from "@ui-kit/Dropdown";
import { GlassToolbarButton, GlassToolbarIcon, GlassToolbarTriggerChevron } from "@ui-kit/GlassToolbar";
import { Icon } from "@ui-kit/Icon";
import { Tooltip, TooltipContent, TooltipTrigger } from "@ui-kit/Tooltip";

interface NoteMenuActionsProps {
	editor: Editor;
	node: Node;
	getPos: () => number;
	updateAttributes: (attributes: Record<string, unknown>, transaction?: boolean) => void;
}

const NoteMenuActions = (props: NoteMenuActionsProps) => {
	const { editor, node, getPos, updateAttributes } = props;
	const hasTitle = node.firstChild?.type.name === "noteTitle";
	const titleTooltip = t("title");
	const typeTooltip = t("type");
	const collapseTooltip = t("collapse");

	const updateType = (type: NoteType) => {
		updateAttributes({ type });
	};

	const addTitle = (text = "") => {
		const pos = getPos() + 1;
		editor
			.chain()
			.insertContentAt(pos, { type: "noteTitle", content: text ? [{ type: "text", text }] : [] })
			.focus(pos + 1, { scrollIntoView: false })
			.run();
	};

	const removeTitle = () => {
		const titleNode = node.firstChild;
		if (titleNode?.type.name !== "noteTitle") return;
		const from = getPos() + 1;
		editor
			.chain()
			.deleteRange({ from, to: from + titleNode.nodeSize })
			.focus(from, { scrollIntoView: false })
			.run();
	};

	const toggleHeadEditor = () => {
		if (hasTitle) removeTitle();
		else addTitle();
	};

	const toggleCollapse = () => {
		const collapsed = !node.attrs.collapsed;
		updateAttributes({ collapsed });
		if (collapsed && !hasTitle) addTitle(t("more"));
	};

	return (
		<>
			<GlassToolbarButton
				aria-label={titleTooltip}
				aria-pressed={hasTitle}
				data-state={hasTitle ? "on" : "off"}
				onClick={toggleHeadEditor}
				tooltipText={titleTooltip}
			>
				<GlassToolbarIcon icon="heading" />
			</GlassToolbarButton>
			<DropdownMenu>
				<Tooltip>
					<TooltipTrigger asChild>
						<DropdownMenuTrigger asChild>
							<GlassToolbarTriggerChevron aria-label={typeTooltip}>
								<GlassToolbarIcon icon={noteIcons[node.attrs.type] as IconCode} />
							</GlassToolbarTriggerChevron>
						</DropdownMenuTrigger>
					</TooltipTrigger>
					<TooltipContent sideOffset={2}>{typeTooltip}</TooltipContent>
				</Tooltip>
				<DropdownMenuContent>
					<DropdownMenuRadioGroup
						indicatorIconPosition="end"
						onValueChange={updateType}
						value={node.attrs.type}
					>
						{Object.values(NoteType).map(
							(value) =>
								value !== NoteType.hotfixes && (
									<DropdownMenuRadioItem key={value} value={value}>
										<Icon color={getIconColor(value)} icon={noteIcons[value]} />
										<span>{t(`${value}-text`)}</span>
									</DropdownMenuRadioItem>
								),
						)}
					</DropdownMenuRadioGroup>
				</DropdownMenuContent>
			</DropdownMenu>
			<GlassToolbarButton
				aria-label={collapseTooltip}
				aria-pressed={node.attrs.collapsed}
				data-state={node.attrs.collapsed ? "on" : "off"}
				onClick={toggleCollapse}
				tooltipText={collapseTooltip}
			>
				<GlassToolbarIcon icon="chevrons-down-up" />
			</GlassToolbarButton>
		</>
	);
};

export default NoteMenuActions;
